import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'custom-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.css'],
})
export class TimePickerComponent implements OnInit {
  @Input() initialTime: {
    hour: number;
    minute: number;
    period?: 'AM' | 'PM';
  } | null = null;
  @Output() timeSelected = new EventEmitter<{
    hour: number;
    minute: number;
    period: 'AM' | 'PM';
  }>();
  @Output() errorStatus = new EventEmitter<boolean>();

  timeForm: FormGroup;
  period: 'AM' | 'PM' = 'AM';

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();

    if (this.initialTime) {
      this.prefillTime(this.initialTime);
    }
    this.handleFormChanges();
  }

  // Set AM/PM Period
  setPeriod(period: 'AM' | 'PM'): void {
    this.period = period;
    this.emitTime();
  }

  validateInput(): void {
    if (this.timeForm.valid) {
      this.emitTime();
    } else {
      this.errorStatus.emit(true); // Emit error status if form is invalid
    }
  }

  // Initialize Form
  private initForm(): void {
    this.timeForm = this.fb.group({
      hour: ['', [Validators.required, Validators.min(0), Validators.max(12)]],
      minute: [
        '',
        [Validators.required, Validators.min(0), Validators.max(59)],
      ],
    });
  }

  // Prefill Time Based on Input
  private prefillTime(initialTime: {
    hour: number;
    minute: number;
    period?: 'AM' | 'PM';
  }): void {
    const { hour, minute, period } = initialTime;

    if (hour >= 0 && hour <= 23) {
      const hour12 = hour % 12 || 12; // Convert to 12-hour format
      if (period) {
        this.period = period;
      } else {
        this.period = hour >= 12 ? 'PM' : 'AM';
      }
      this.timeForm.patchValue({ hour: hour12, minute });
    }
  }

  private handleFormChanges(): void {
    this.timeForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
      )
      .subscribe(() => {
        if (this.timeForm?.valid) {
          this.emitTime();
        } else {
          this.errorStatus.emit(true);
        }
      });
  }

  // Check for Errors in Form Control
  hasError(controlName: string): boolean {
    const control = this.timeForm.get(controlName);
    return control?.invalid && control?.touched;
  }

  // Emit Selected Time
  private emitTime(): void {
    const { hour, minute } = this.timeForm.value;
    if (this.timeForm.valid) {
      this.timeSelected.emit({
        hour: hour % 12 || 12, // Always output 12-hour format
        minute: Number(minute),
        period: this.period,
      });
      this.errorStatus.emit(false);
    }
  }
}
